// frontend/src/components/Auth/Login.js

import React, { useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  Stack,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Google as GoogleIcon,
  ArrowBack as ArrowBackIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
} from '@mui/icons-material';
import { useThemeMode } from '../../contexts/ThemeContext';

const Login = () => {
  const theme = useTheme();
  const { darkMode, toggleDarkMode } = useThemeMode();
  const emailRef = useRef();
  const passwordRef = useRef();
  const { login, loginWithGoogle, resetPassword } = useAuth();
  const navigate = useNavigate();

  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setError('');
      setLoading(true);
      await login(emailRef.current.value, passwordRef.current.value);
      navigate('/sessions');
    } catch (error) {
      setError('Failed to log in. Please check your credentials.');
      console.error('Login failed:', error.message);
    }
    setLoading(false);
  };

  const handleGoogleLogin = async () => {
    try {
      setError('');
      setLoading(true);
      await loginWithGoogle();
      navigate('/sessions');
    } catch (error) {
      console.error('Google login failed:', error);
      
      if (error.code === 'auth/popup-closed-by-user') {
        setError('Sign-in was cancelled');
      } else if (error.code === 'auth/popup-blocked') {
        setError('Popup was blocked. Please allow popups and try again');
      } else if (error.code === 'auth/operation-not-allowed') {
        setError('Google Sign-In is not enabled. Please contact support.');
      } else if (error.code === 'auth/unauthorized-domain') {
        setError('This domain is not authorized for Google Sign-In');
      } else {
        setError(`Failed to sign in with Google: ${error.message}`);
      }
    }
    setLoading(false);
  };

  const handleForgotPassword = async () => {
    if (!emailRef.current.value) {
      return setError('Please enter your email address first');
    }

    try {
      setMessage('');
      setError('');
      await resetPassword(emailRef.current.value);
      setMessage('Check your inbox for password reset instructions');
    } catch (error) {
      console.error('Password reset failed:', error);
      setError('Failed to reset password. Please check your email address.');
    }
  };

  return (
    <>
      <Helmet>
        <title>Login - Blackmount</title>
        <meta name="description" content="Sign in to Blackmount to access your browser sessions, notes, and projects across all devices." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        {/* Top Navigation */}
      <Box sx={{ 
        p: 2, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between'
      }}>
        <Button
          startIcon={<ArrowBackIcon />}
          component={Link}
          to="/"
          sx={{ color: 'text.secondary' }}
        >
          Back to Blackmount
        </Button>
        
        <IconButton onClick={toggleDarkMode}>
          {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
        </IconButton>
      </Box>

      {/* Main Content */}
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Card 
          elevation={0}
          sx={{ 
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 3,
            overflow: 'visible',
          }}
        >
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography 
                variant="h4" 
                fontWeight={700}
                sx={{ 
                  mb: 1,
                  background: 'linear-gradient(45deg, #2563eb, #7c3aed)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Welcome back
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Sign in to your Blackmount account
              </Typography>
            </Box>

            {/* Alerts */}
            {error && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                {error}
              </Alert>
            )}
            {message && (
              <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
                {message}
              </Alert>
            )}

            {/* Google Sign-in */}
            <Button
              fullWidth
              variant="outlined"
              size="large"
              startIcon={<GoogleIcon />}
              onClick={handleGoogleLogin}
              disabled={loading}
              sx={{
                py: 1.5,
                mb: 3,
                borderRadius: 3,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 500,
                color: 'text.primary',
                borderColor: 'divider',
                '&:hover': {
                  borderColor: 'primary.main',
                },
              }}
            >
              {loading ? 'Signing in...' : 'Continue with Google'}
            </Button>

            {/* Divider */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              my: 3,
              '&::before, &::after': {
                content: '""',
                flex: 1,
                height: '1px',
                bgcolor: 'divider',
              },
            }}>
              <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
                or
              </Typography>
            </Box>

            {/* Email Form */}
            <Box component="form" onSubmit={handleSubmit}>
              <Stack spacing={3}>
                <TextField
                  label="Email address"
                  type="email"
                  inputRef={emailRef}
                  required
                  fullWidth
                  autoComplete="email"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                />

                <TextField
                  label="Password"
                  type="password"
                  inputRef={passwordRef}
                  required
                  fullWidth
                  autoComplete="current-password"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{
                    py: 1.5,
                    borderRadius: 3,
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 600,
                  }}
                >
                  {loading ? 'Signing in...' : 'Sign in'}
                </Button>
              </Stack>
            </Box>

            {/* Forgot Password */}
            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Button
                variant="text"
                onClick={handleForgotPassword}
                disabled={loading}
                sx={{ textTransform: 'none', color: 'text.secondary' }}
              >
                Forgot your password?
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Sign up link */}
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Don't have an account?{' '}
            <Button
              component={Link}
              to="/signup"
              variant="text"
              sx={{ 
                textTransform: 'none',
                fontWeight: 600,
                p: 0,
                minWidth: 'auto',
              }}
            >
              Sign up
            </Button>
          </Typography>
        </Box>
      </Container>
    </Box>
    </>
  );
};

export default Login;

